import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Lock, Database, UserCheck } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/terms" className="flex items-center gap-2 text-slate-400 hover:text-[#FF5F00] transition-colors">
            <FileText className="w-5 h-5" />
            <span>Terms of Service</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-[#FF5F00]/10 rounded-xl">
            <Shield className="w-8 h-8 text-[#FF5F00]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
            <p className="text-slate-400">Effective Date: February 15, 2026</p>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none">
          {/* Section 2.1 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-[#FF5F00]" />
              <span><span className="text-[#FF5F00]">2.1</span> Information We Collect</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              To provide a seamless experience for tradesmen and home service providers, we collect:
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Account Data:</strong> Name, business email, and billing information.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Operational Data:</strong> Job schedules, customer contact details, and invoice history.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Media & Files:</strong> Images of job sites, digital receipts, and expense documentation.</span>
              </li>
            </ul>
          </section>

          {/* Section 2.2 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#FF5F00]" />
              <span><span className="text-[#FF5F00]">2.2</span> How We Use Your Data</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We use your data strictly to:
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>Facilitate the core functions of the MyJobTools platform.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>Process payments and provide customer support.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>Improve platform performance and security.</span>
              </li>
            </ul>
          </section>

          {/* Section 2.3 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#FF5F00]" />
              <span><span className="text-[#FF5F00]">2.3</span> Data Storage & Security</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We take data privacy seriously, especially regarding your financial records:
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Encryption:</strong> All uploaded images and receipts are encrypted at rest and in transit.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Third-Party Disclosure:</strong> We do not sell your data. We only share data with essential third-party service providers (e.g., payment processors like Stripe) necessary to run the service.</span>
              </li>
            </ul>
          </section>

          {/* Section 2.4 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF5F00]" />
              <span><span className="text-[#FF5F00]">2.4</span> Your Rights</span>
            </h2>
            <p className="text-slate-300 leading-relaxed">
              You have the right to access, export, or delete your data at any time. Upon cancellation of your subscription, we will retain your data for a limited period (as required by law) before permanent deletion.
            </p>
          </section>

          {/* Contact Section */}
          <section className="bg-gradient-to-r from-[#FF5F00]/10 to-transparent rounded-xl p-6 border border-[#FF5F00]/30">
            <h2 className="text-xl font-semibold text-white mb-4">Questions About Your Privacy?</h2>
            <p className="text-slate-300 leading-relaxed">
              If you have any questions about this Privacy Policy or how we handle your data, please contact us at{' '}
              <a href="mailto:privacy@myjobtools.com" className="text-[#FF5F00] hover:underline">
                privacy@myjobtools.com
              </a>
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-700 flex flex-col sm:flex-row gap-4 justify-between items-center">
          <Link to="/terms" className="text-[#FF5F00] hover:text-[#FF5F00]/80 transition-colors flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Read our Terms of Service
          </Link>
          <Link to="/register" className="bg-[#FF5F00] text-white px-6 py-3 rounded-lg hover:bg-[#FF5F00]/90 transition-colors font-medium">
            Get Started Free
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
